import isEqual from 'lodash-es/isEqual';
import omit from 'lodash-es/omit';

import { type ActionTypes } from '~/actions';
import {
  type CreateAPIAddressSuccessPayload,
  type FetchAddressesPayload,
} from '~/actions/address.actions';
import { ActionKeys } from '~/actions/constants';
import { type FetchUserPayload } from '~/actions/user.actions';
import { RETURN_ADDRESS_KIND, SENDER_ADDRESS_KIND } from '~/constants/addressBook';
import { type SavedAPIAddress } from '~/types/APIAddress';
import {
  areAddressesFullyEqual,
  isBaseAddress,
  type AddressType,
  type BaseAddress,
} from '~/types/Address';
import { type OrderSelection } from '~/types/Order';
import { listToMapById } from '~/utils/list';

export type AddressHash = HashMap<string, Maybe<BaseAddress | number>>;

export type APIAddressHash = HashMap<number, SavedAPIAddress>;

type SelectAddressAction = { key: AddressType; orderSelection?: OrderSelection; type: 'select' };

type EditAddressAction = {
  isMultipleAddresses?: boolean;
  key: 'create' | 'edit' | AddressType;
  orderSelection?: OrderSelection;
  type: 'edit';
};

type CreateAddressAction = {
  key?: 'create' | 'edit' | AddressType;
  orderSelection?: OrderSelection;
  type: 'create';
};

export type AddressBookAction = CreateAddressAction | EditAddressAction | SelectAddressAction;

type AddressBookInstance = {
  readonly combineSenderReturnAddresses: boolean;
  readonly currentAction: Maybe<AddressBookAction>;
  readonly initialized: boolean;
  readonly selectedAddresses: AddressHash;
};

export type AddressBookInstances = HashMap<string, AddressBookInstance>;

export type AddressState = {
  // AddressBook state
  readonly addressBookInstances: AddressBookInstances;
  readonly apiAddresses: APIAddressHash;
  readonly errorMessage: string;
  readonly isCreatingApiAddress: boolean; // TODO: Maybe rely on form isSubmitting?
  readonly isDeletingApiAddress: boolean;
  readonly isLoadedApiAddresses: boolean;
  // TODO: How to avoid death by a thousand booleans?
  readonly isLoadingApiAddresses: boolean;
  readonly isUpdatingApiAddress: boolean; // TODO: Maybe rely on form isSubmitting?
};

const addressBookInitialState = {
  combineSenderReturnAddresses: false,
  currentAction: null,
  initialized: false,
  selectedAddresses: {},
};

const initialState: AddressState = {
  // AddressBook state
  addressBookInstances: {},
  apiAddresses: {},
  errorMessage: '',
  isCreatingApiAddress: false,
  isDeletingApiAddress: false,
  isLoadedApiAddresses: false,
  isLoadingApiAddresses: false,
  isUpdatingApiAddress: false,
};

const clearAddressBookCurrentAction = (
  addressBookInstances: AddressBookInstances,
): AddressBookInstances =>
  Object.keys(addressBookInstances).reduce(
    (instances, bookKey) => ({
      ...instances,
      [bookKey]: { ...addressBookInstances[bookKey], currentAction: null },
    }),
    {},
  );

const addressReducer = (state: AddressState = initialState, action: ActionTypes): AddressState => {
  switch (action.type) {
    case ActionKeys.ADDRESSES_FETCH:
      return {
        ...state,
        isLoadingApiAddresses: true,
      };
    case ActionKeys.ADDRESSES_FETCH_SUCCESS:
      const { addresses: addressesFromPayload } = action.payload as FetchAddressesPayload;
      return {
        ...state,
        apiAddresses: <APIAddressHash>listToMapById(addressesFromPayload),
        isLoadedApiAddresses: true,
        isLoadingApiAddresses: false,
      };
    case ActionKeys.ADDRESSES_FETCH_ERROR:
      return {
        ...state,
        errorMessage: action.payload.error,
        isLoadedApiAddresses: false,
        isLoadingApiAddresses: false,
      };
    case ActionKeys.APIADDRESS_CREATE:
      return {
        ...state,
        isCreatingApiAddress: true,
      };
    case ActionKeys.APIADDRESS_CREATE_SUCCESS: {
      const { address: newAddress } = action.payload as CreateAPIAddressSuccessPayload;
      const { isDefaultReturn, isDefaultSender } = newAddress;

      if (!isDefaultSender && !isDefaultReturn) {
        return {
          ...state,
          addressBookInstances: clearAddressBookCurrentAction(state.addressBookInstances),
          apiAddresses: { ...state.apiAddresses, [newAddress.id]: newAddress },
          isCreatingApiAddress: false,
        };
      }

      const newApiAddresses = Object.values(state.apiAddresses).reduce(
        (newValues, address) => ({
          ...newValues,
          [address.id]: {
            ...address,
            isDefaultReturn: isDefaultReturn ? false : address.isDefaultReturn,
            isDefaultSender: isDefaultSender ? false : address.isDefaultSender,
          },
        }),
        {},
      );

      return {
        ...state,
        addressBookInstances: clearAddressBookCurrentAction(state.addressBookInstances),
        apiAddresses: { ...newApiAddresses, [newAddress.id]: newAddress },
        isCreatingApiAddress: false,
      };
    }
    case ActionKeys.APIADDRESS_CREATE_ERROR:
      return {
        ...state,
        errorMessage: action.payload.error,
        isCreatingApiAddress: false,
      };
    case ActionKeys.APIADDRESS_EDIT:
      return {
        ...state,
        errorMessage: '',
        isUpdatingApiAddress: true,
      };
    case ActionKeys.APIADDRESS_EDIT_SUCCESS: {
      const updatedAddress = action.payload.address;
      const { isDefaultReturn, isDefaultSender } = updatedAddress;

      const apiAddresses = Object.values(state.apiAddresses).reduce(
        (newAddresses, address) =>
          address.id === updatedAddress.id
            ? {
                ...newAddresses,
                [updatedAddress.id]: updatedAddress,
              }
            : {
                ...newAddresses,
                [address.id]: {
                  ...address,
                  isDefaultReturn: isDefaultReturn ? false : address.isDefaultReturn,
                  isDefaultSender: isDefaultSender ? false : address.isDefaultSender,
                },
              },
        {},
      );

      return {
        ...state,
        addressBookInstances: clearAddressBookCurrentAction(state.addressBookInstances),
        apiAddresses,
        isUpdatingApiAddress: false,
      };
    }
    case ActionKeys.APIADDRESS_EDIT_ERROR:
      return {
        ...state,
        errorMessage: action.payload.error,
        isUpdatingApiAddress: false,
      };
    case ActionKeys.APIADDRESS_DELETE:
      return {
        ...state,
        errorMessage: '',
        isDeletingApiAddress: true,
      };
    case ActionKeys.APIADDRESS_DELETE_ERROR:
      return {
        ...state,
        errorMessage: action.payload.error,
        isDeletingApiAddress: false,
      };
    case ActionKeys.APIADDRESS_DELETE_SUCCESS:
      // TODO: What should be the selected address after delete?
      // We cannot rely on defaulting via the key if return or sender because that would
      // be hardcoding the shit out of this and that is not needed for other functionality
      // like addresses settings

      return {
        ...state,
        addressBookInstances: clearAddressBookCurrentAction(state.addressBookInstances),
        apiAddresses: omit(state.apiAddresses, [action.payload.id]),
        isDeletingApiAddress: false,
      };
    case ActionKeys.APIADDRESS_CLEAR_ERROR:
      return {
        ...state,
        errorMessage: '',
      };
    case ActionKeys.ADDRESS_BOOK_ACTION:
      return {
        ...state,
        addressBookInstances: {
          ...state.addressBookInstances,
          [action.payload.addressBookName]: {
            ...state.addressBookInstances[action.payload.addressBookName],
            currentAction: action.payload.action,
          },
        },
      };
    case ActionKeys.ADDRESS_BOOK_INITIALIZE:
      if (
        state.addressBookInstances[action.payload.addressBookName] &&
        state.addressBookInstances[action.payload.addressBookName].initialized
      )
        return state;

      const addresses = action.payload.addresses;
      const addressesKeys = Object.keys(addresses);
      const senderAddress = addresses[SENDER_ADDRESS_KIND];
      const returnAddress = addresses[RETURN_ADDRESS_KIND];

      // If both `senderAddress` and `returnAddress` are <BaseAddress>, compare them as such
      const areAddressesEqual =
        senderAddress &&
        isBaseAddress(senderAddress) &&
        returnAddress &&
        isBaseAddress(returnAddress) &&
        areAddressesFullyEqual(senderAddress, returnAddress);

      return {
        ...state,
        addressBookInstances: {
          ...state.addressBookInstances,
          [action.payload.addressBookName]: {
            ...addressBookInitialState,
            combineSenderReturnAddresses:
              action.payload.combineSenderReturnAddresses !== undefined
                ? action.payload.combineSenderReturnAddresses
                : addressesKeys.length > 0 && // Check for settings/address that initializes with no address
                  (areAddressesEqual || isEqual(senderAddress, returnAddress)),
            initialized: true,
            selectedAddresses: action.payload.addresses,
          },
        },
      };
    case ActionKeys.ADDRESS_BOOK_SELECT: {
      const addressBook = state.addressBookInstances[action.payload.addressBookName];
      if (!addressBook) return state;

      const selectedAddresses = addressBook.selectedAddresses;
      return {
        ...state,
        addressBookInstances: {
          ...state.addressBookInstances,
          [action.payload.addressBookName]: {
            ...addressBook,
            currentAction: null,
            selectedAddresses:
              addressBook.combineSenderReturnAddresses && action.payload.key === SENDER_ADDRESS_KIND
                ? {
                    ...selectedAddresses,
                    [RETURN_ADDRESS_KIND]: action.payload.address,
                    [action.payload.key]: action.payload.address,
                  }
                : {
                    ...selectedAddresses,
                    [action.payload.key]: action.payload.address,
                  },
          },
        },
      };
    }
    case ActionKeys.ADDRESS_BOOK_CLEAR_SELECTION: {
      const addressBook = state.addressBookInstances[action.payload.addressBookName];
      if (!addressBook) return state;

      const selectedAddresses = addressBook.selectedAddresses;

      return {
        ...state,
        addressBookInstances: {
          ...state.addressBookInstances,
          [action.payload.addressBookName]: {
            ...addressBook,
            currentAction: null,
            selectedAddresses: {
              ...selectedAddresses,
              [action.payload.key]: null,
            },
          },
        },
      };
    }
    case ActionKeys.ADDRESS_BOOK_SELECT_DEFAULT:
      const { addressBookName, key } = action.payload;

      if (!Object.keys(state.addressBookInstances[addressBookName].selectedAddresses).includes(key))
        return state;

      const apiAddresses = Object.values(state.apiAddresses);
      const defaultSenderAddress = apiAddresses.find((x: SavedAPIAddress) => !!x.isDefaultSender);
      const defaultReturnAddress = apiAddresses.find((x: SavedAPIAddress) => !!x.isDefaultReturn);

      const defaults: Record<string, typeof defaultSenderAddress> = {
        return: defaultReturnAddress,
        sender: defaultSenderAddress,
      };

      if (
        state.addressBookInstances[addressBookName].selectedAddresses[SENDER_ADDRESS_KIND] ===
        state.addressBookInstances[addressBookName].selectedAddresses[RETURN_ADDRESS_KIND]
      ) {
        return {
          ...state,
          addressBookInstances: {
            ...state.addressBookInstances,
            [addressBookName]: {
              ...state.addressBookInstances[addressBookName],
              selectedAddresses: {
                ...state.addressBookInstances[addressBookName].selectedAddresses,
                return: defaults.return ? defaults.return.id! : null,
                sender: defaults.sender ? defaults.sender.id! : null,
              },
            },
          },
        };
      }

      return {
        ...state,
        addressBookInstances: {
          ...state.addressBookInstances,
          [action.payload.addressBookName]: {
            ...state.addressBookInstances[action.payload.addressBookName],
            selectedAddresses: {
              ...state.addressBookInstances[action.payload.addressBookName].selectedAddresses,
              [key]: defaults[key] ? defaults[key]?.id : null,
            },
          },
        },
      };

    case ActionKeys.ADDRESS_BOOK_TOGGLE_COMBINE_SENDER_RETURN_ADDRESSES: {
      const addressBook = state.addressBookInstances[action.payload.addressBookName];
      if (!addressBook) return state;

      const { selectedAddresses } = addressBook;
      const updatedCombine = !addressBook.combineSenderReturnAddresses;

      return {
        ...state,
        addressBookInstances: {
          ...state.addressBookInstances,
          [action.payload.addressBookName]: {
            ...addressBook,
            combineSenderReturnAddresses: updatedCombine,
            selectedAddresses: updatedCombine
              ? {
                  ...selectedAddresses,
                  [RETURN_ADDRESS_KIND]: selectedAddresses[SENDER_ADDRESS_KIND],
                }
              : selectedAddresses,
          },
        },
      };
    }
    case ActionKeys.ADDRESS_BOOK_CLEAR_STATE:
      return {
        ...state,
        addressBookInstances: {
          ...Object.keys(state.addressBookInstances).reduce((instances, key) => {
            if (key === action.payload.addressBookName) return instances;

            return { ...instances, [key]: state.addressBookInstances[key] };
          }, {}),
        },
      };
    case ActionKeys.USER_FETCH_SUCCESS:
      const userFetchPayload: FetchUserPayload = action.payload;
      const defaultSenderAddressId: Maybe<number> = userFetchPayload.user.address
        ? userFetchPayload.user.address.id
          ? userFetchPayload.user.address.id
          : null
        : null;
      const defaultReturnAddressId: Maybe<number> = userFetchPayload.user.addressReturn
        ? userFetchPayload.user.addressReturn.id
          ? userFetchPayload.user.addressReturn.id
          : null
        : null;

      const updatedAddresses = Object.values(state.apiAddresses).reduce((updated, apiAddress) => {
        if (defaultSenderAddressId && apiAddress.id === defaultSenderAddressId)
          return { ...updated, [defaultSenderAddressId]: userFetchPayload.user.address };

        if (defaultReturnAddressId && apiAddress.id === defaultReturnAddressId)
          return { ...updated, [defaultReturnAddressId]: userFetchPayload.user.addressReturn };

        return { ...updated, [apiAddress.id]: apiAddress };
      }, {});

      return {
        ...state,
        apiAddresses: updatedAddresses,
      };

    default:
      return state;
  }
};

export default addressReducer;
